import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { CatalogDtosKeysEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-dtos-keys.enum';
import { SaasDtoKeysEnum } from '@zaoblako/shared/modules/saas/enums/saas-dto-keys.enum';
import { AgentTypeEnum } from '@zaoblako/shared/modules/ai/enums/agent-type.enum';
import { AgentOperatingModeEnum } from '@zaoblako/shared/modules/ai/enums/agent-operating-mode.enum';
import { booleanSchema } from '@zaoblako/shared/modules/serde/schemas/boolean.schema';
import { KnowledgeBaseCategoryEnum } from '@zaoblako/shared/modules/ai/enums/knowledge-base-category.enum';
import type { TenantFindInterface } from '@zaoblako/shared/modules/saas/dtos/tenant-find.dto';

import * as yup from 'yup';

/** Agent API request body */
export interface AgentFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;
  /** Creation date and time */
  created: Date;
  /** Last update date and time */
  updated: Date;
  /** Soft deletion update date and time. The agent will be permanently deleted six months after this date. */
  deleted?: Date;
  /** Agent display name */
  name: string;
  /** Agent model name */
  model?: string;
  /** Agent type */
  type: AgentTypeEnum;
  /** Agent operating mode */
  operatingMode: AgentOperatingModeEnum;
  /** Agent prompt */
  prompt: string;
  /** Accuracy of answers (range by 0.0 - 1.0) */
  temperature: number;
  /** Minimum reliability for responses (range by 0.0 - 1.0) */
  minConfidence: number;
  /** Active agent */
  isActive: boolean;
  /** Agent has embedding function */
  isEmbedding: boolean;
  /** Default agent for embeddings when the active agent has no embedding */
  isEmbeddingDefault: boolean;
  /** Allows the agent to automatically answer questions */
  autoReply: boolean;
  /** Agent api key (only when explicitly requested, e.g. catalog edit form) */
  apiKey?: string;
  /** If `true` then this agent will be used by voice assistant */
  isVoiceAssistantDefault?: boolean;
  /** Agent tenant */
  tenant: TenantFindInterface;
  /** Agent folder id */
  folderID?: string;
  /** Agent base url */
  baseURL?: string;
  /** Agent search categories */
  searchCategories?: KnowledgeBaseCategoryEnum[];
}

/** Agent API request body - DTO schema */
export const AgentFindDto = SerdeDtoClass.create<AgentFindInterface>(CatalogDtosKeysEnum.AGENT_FIND_DTO, {
  id: {
    label: 'Номер',
    serializer: yup.number(),
  },
  created: {
    sortable: true,
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    sortable: true,
    label: 'Изменён',
    serializer: yup.date(),
  },
  deleted: {
    optional: true,
    label: 'Удалён',
    serializer: yup.date(),
  },
  name: {
    sortable: true,
    label: 'Название',
    serializer: yup.string(),
  },
  model: {
    optional: true,
    label: 'Модель',
    serializer: yup.string(),
  },
  type: {
    sortable: true,
    label: 'Тип',
    serializer: yup
      .string()
      .oneOf(Object.values(AgentTypeEnum)),
  },
  operatingMode: {
    sortable: true,
    label: 'Режим работы',
    serializer: yup
      .string()
      .oneOf(Object.values(AgentOperatingModeEnum)),
  },
  prompt: {
    label: 'Инструкция',
    hint: 'Инструкция-шаблон для агента ИИ, по которой он будет работать',
    serializer: yup.string(),
  },
  temperature: {
    sortable: true,
    label: 'Степень случайности и креативности',
    serializer: yup.number(),
  },
  minConfidence: {
    sortable: true,
    label: 'Минимальная уверенность для ответа',
    serializer: yup.number(),
  },
  isActive: {
    sortable: true,
    label: 'Параметр активности',
    serializer: booleanSchema,
  },
  isEmbedding: {
    sortable: true,
    label: 'Агент имеет функцию эмбеддинга',
    serializer: booleanSchema,
  },
  isEmbeddingDefault: {
    sortable: true,
    label: 'Дефолтный агент для эмбеддинга',
    serializer: booleanSchema,
  },
  autoReply: {
    sortable: true,
    label: 'Разрешение на автоматический ответ без участия человека',
    serializer: booleanSchema,
  },
  apiKey: {
    optional: true,
    label: 'Ключ доступа',
    serializer: yup.string(),
  },
  isVoiceAssistantDefault: {
    optional: true,
    label: 'Агент по умолчанию для голосового помощника',
    serializer: booleanSchema,
  },
  tenant: {
    sortable: true,
    label: 'Тенант',
    nested: SaasDtoKeysEnum.TENANT_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  folderID: {
    optional: true,
    label: 'FolderID',
    serializer: yup.string(),
  },
  baseURL: {
    optional: true,
    label: 'Базовый URL',
    serializer: yup.string(),
  },
  searchCategories: {
    optional: true,
    sortable: true,
    label: 'Категории поиска',
    serializer: yup.array(
      yup
        .string()
        .oneOf(Object.values(KnowledgeBaseCategoryEnum)),
    ),
  },
});
